import React, { useMemo, useCallback } from 'react';
import { ConnectionProvider, WalletProvider } from '@solana/wallet-adapter-react';
import { WalletAdapterNetwork } from '@solana/wallet-adapter-base';
import { WalletModalProvider } from '@solana/wallet-adapter-react-ui';
import { PhantomWalletAdapter } from '@solana/wallet-adapter-phantom';
import { SolflareWalletAdapter } from '@solana/wallet-adapter-solflare';
import { CoinbaseWalletAdapter } from '@solana/wallet-adapter-coinbase';
import { TrustWalletAdapter } from '@solana/wallet-adapter-trust';
import { clusterApiUrl } from '@solana/web3.js';

import '@solana/wallet-adapter-react-ui/styles.css';

export const SolanaProvider = ({ children }) => {
  // Use mainnet for the real token
  const network = WalletAdapterNetwork.Mainnet;
  const endpoint = useMemo(() => clusterApiUrl(network), [network]);

  // Configure all supported wallets
  const wallets = useMemo(
    () => [
      new PhantomWalletAdapter(),
      new SolflareWalletAdapter(),
      new CoinbaseWalletAdapter(),
      new TrustWalletAdapter(),
    ],
    []
  );

  // Centralized wallet error handler — keeps UX clean when users
  // dismiss connect modal, deny permission, or have no wallet installed.
  // Common silent / expected errors are demoted to console.info so they
  // don't pollute logs; everything else logs as a warning with structured
  // info for debugging.
  const onWalletError = useCallback((error /* , adapter */) => {
    const name = error?.name || '';
    const msg = error?.message || String(error);
    const benign = [
      'WalletNotReadyError',         // wallet extension not installed
      'WalletConnectionError',       // user denied or closed wallet popup
      'WalletWindowClosedError',     // user closed wallet popup
      'WalletNotSelectedError',      // user closed our select modal
    ];
    if (benign.some((b) => name === b || msg.includes(b))) {
      console.info('[wallet] benign:', name, msg);
      return;
    }
    console.warn('[wallet] error:', name, msg);
  }, []);

  return (
    <ConnectionProvider endpoint={endpoint}>
      <WalletProvider wallets={wallets} onError={onWalletError} autoConnect>
        <WalletModalProvider>
          {children}
        </WalletModalProvider>
      </WalletProvider>
    </ConnectionProvider>
  );
};

export default SolanaProvider;
